/* FOOTBALL-THEME :: Slideshow.
   Vanilla <football-slideshow> custom element: arrows, dots, autoplay,
   pointer drag and a Ken-Burns class on the active slide.

   Motion runs on fsPhysics springs (assets/football-physics.js): the track
   follows the finger 1:1, a flick is projected forward and thrown with the
   finger's release velocity handed to the spring, and a settle can be
   grabbed and reversed mid-flight. Rubber-band at the far edges.

   Autoplay pauses on hover/focus, when the tab is hidden, and is disabled
   entirely under prefers-reduced-motion (settles become instant jumps).
   Depends on window.fsPhysics. No other dependencies. */
(function () {
  'use strict';

  if (customElements.get('football-slideshow')) return;

  class FootballSlideshow extends HTMLElement {
    connectedCallback() {
      this._init();
    }

    disconnectedCallback() {
      this._stop();
      if (this._spring) this._spring.stop();
      document.removeEventListener('visibilitychange', this._onVisibility);
      window.removeEventListener('resize', this._onResize);
    }

    _init() {
      if (this._inited) return;
      this._inited = true;

      this.track = this.querySelector('.fs-slideshow__track');
      this.frame = this.querySelector('.fs-slideshow__frame');
      this.slides = Array.prototype.slice.call(
        this.querySelectorAll('.fs-slideshow__slide')
      );
      this.dots = Array.prototype.slice.call(
        this.querySelectorAll('.fs-slideshow__dot')
      );
      this.prev = this.querySelector('.fs-slideshow__arrow--prev');
      this.next = this.querySelector('.fs-slideshow__arrow--next');
      this.count = this.slides.length;
      this.realCount = this.count;

      this.index = 0;
      this.timer = null;
      this.paused = false;
      this.dragging = false;
      this._live = 0;
      this._dragOffset = null;

      var speed = parseFloat(this.getAttribute('data-speed'));
      this.speed = (isNaN(speed) ? 5 : speed) * 1000;
      this.wantsAutoplay =
        this.hasAttribute('data-autoplay') &&
        !window.fsPhysics.reduced() &&
        this.count > 1;

      if (!this.track || this.count === 0) return;

      var self = this;
      this._spring = window.fsPhysics.spring({
        from: 0,
        to: 0,
        damping: 1,
        response: 0.45,
        onUpdate: function (value) {
          self.track.style.transform = 'translateX(' + -value + 'px)';
        }
      });

      this._onEnter = this._onEnter.bind(this);
      this._onLeave = this._onLeave.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onResize = this._onResize.bind(this);
      this._finishWrap = this._finishWrap.bind(this);

      if (this.prev) {
        this.prev.addEventListener('click', () => this.goTo(this.index - 1));
      }
      if (this.next) {
        this.next.addEventListener('click', () => this.goTo(this.index + 1));
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(parseInt(dot.getAttribute('data-index'), 10) || 0);
        });
      });

      if (this.frame) {
        this.frame.addEventListener('pointerdown', this._onPointerDown);
      }
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);

      this.addEventListener('mouseenter', this._onEnter);
      this.addEventListener('mouseleave', this._onLeave);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);
      document.addEventListener('visibilitychange', this._onVisibility);
      window.addEventListener('resize', this._onResize);

      this._buildLoop();
      this.goTo(0, true);
      this._start();
    }

    _buildLoop() {
      if (this.realCount < 2 || !this.track) return;

      var first = this.slides[0].cloneNode(true);
      var last = this.slides[this.realCount - 1].cloneNode(true);

      [first, last].forEach(function (clone) {
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, input, [tabindex]').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
      });

      this.track.insertBefore(last, this.track.firstChild);
      this.track.appendChild(first);

      this.allSlides = Array.prototype.slice.call(
        this.track.querySelectorAll('.fs-slideshow__slide')
      );
      this.pos = 1;
    }

    /* ---- geometry -------------------------------------------------------- */

    _spacing() {
      if (this.allSlides && this.allSlides.length > 1) {
        return this.allSlides[1].offsetLeft - this.allSlides[0].offsetLeft;
      }
      return this.frame ? this.frame.clientWidth : 0;
    }

    _offsetAt(pos) {
      var all = this.allSlides || this.slides;
      if (!all || !all.length || !this.frame) return 0;
      var ref = Math.min(Math.max(pos, 0), all.length - 1);
      var slide = all[ref];
      var offset =
        slide.offsetLeft - (this.frame.clientWidth - slide.offsetWidth) / 2;
      if (pos !== ref) offset += (pos - ref) * this._spacing();
      return offset;
    }

    _indexForPos(pos) {
      var n = this.realCount;
      if (!n) return 0;
      if (this.allSlides) {
        if (pos === 0) return n - 1;
        if (pos === n + 1) return 0;
        return Math.min(Math.max(pos - 1, 0), n - 1);
      }
      return Math.min(Math.max(pos, 0), n - 1);
    }

    /* ---- rendering ------------------------------------------------------- */

    _updateSlides() {
      var self = this;
      var all = this.allSlides || this.slides;
      var active = all[this.allSlides ? this.pos : this.index];

      all.forEach(function (slide) {
        slide.classList.toggle('is-active', slide === active);
      });

      this.slides.forEach(function (slide, i) {
        slide.setAttribute('aria-hidden', i === self.index ? 'false' : 'true');
      });

      this.dots.forEach(function (dot, i) {
        var on = i === self.index;
        dot.classList.toggle('is-active', on);
        if (on) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    _jumpToPos(pos) {
      this._spring.jump(this._offsetAt(pos));
    }

    _animateToPos(pos, velocity, bounce, onComplete) {
      var target = this._offsetAt(pos);
      if (window.fsPhysics.reduced()) {
        this._spring.jump(target);
        if (onComplete) onComplete();
        return;
      }
      this._spring.retarget(
        target,
        velocity,
        bounce ? 0.85 : 1,
        bounce ? 0.35 : 0.45,
        onComplete
      );
    }

    /* Landed on a cloned edge -> snap (instantly) to the matching real slide. */
    _finishWrap() {
      var n = this.realCount;
      if (this.allSlides && (this.pos === 0 || this.pos === n + 1)) {
        this.pos = this.pos === 0 ? n : 1;
        this.index = this._indexForPos(this.pos);
        this._jumpToPos(this.pos);
        this._updateSlides();
      }
    }

    goTo(target, instant) {
      if (!this.realCount) return;
      var n = this.realCount;
      var real = ((target % n) + n) % n;

      if (instant || !this.allSlides) {
        this.index = real;
        this.pos = this.allSlides ? real + 1 : real;
        this._jumpToPos(this.pos);
        this._updateSlides();
        return;
      }

      var delta = real - this.index;
      if (delta > n / 2) delta -= n;
      if (delta < -n / 2) delta += n;

      this.index = real;
      this.pos = this.pos + delta;
      this._updateSlides();
      this._animateToPos(this.pos, 0, false, this._finishWrap);
      this._start();
    }

    /* ---- autoplay / pause ------------------------------------------------ */

    _start() {
      this._stop();
      if (!this.wantsAutoplay || this.paused || document.hidden || this.dragging) return;
      this.timer = window.setInterval(() => {
        this.goTo(this.index + 1);
      }, this.speed);
    }

    _stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    _onEnter() {
      this.paused = true;
      this._stop();
    }

    _onLeave() {
      this.paused = false;
      this._start();
    }

    _onFocusIn() {
      this.paused = true;
      this._stop();
    }

    _onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.paused = false;
      this._start();
    }

    _onVisibility() {
      if (document.hidden) {
        this._stop();
      } else {
        this._start();
      }
    }

    _onResize() {
      if (!this.track) return;
      this.goTo(this.index, true);
    }

    /* ---- gesture --------------------------------------------------------- */

    _onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('a, button')) return;

      this.dragging = true;
      this._dragOffset = null;
      this._live = this._spring.value; /* presentation value */
      this._spring.stop();
      this.startX = event.clientX;
      this.dx = 0;
      this.classList.add('is-dragging');
      this._velocity = window.fsPhysics.tracker();
      this._velocity.add(event.clientX, performance.now());
      this._stop();

      if (this.frame && this.frame.setPointerCapture) {
        try {
          this.frame.setPointerCapture(event.pointerId);
        } catch (error) {
          /* capture is best-effort */
        }
      }
    }

    _onPointerMove(event) {
      if (!this.dragging || !this.track) return;

      this._velocity.add(event.clientX, performance.now());
      this.dx = event.clientX - this.startX;

      var offset = this._live - this.dx;
      var min = this._offsetAt(0);
      var max = this._offsetAt(this.realCount + 1);
      if (offset < min) {
        offset = min - window.fsPhysics.rubberband(min - offset, this.frame.clientWidth);
      } else if (offset > max) {
        offset = max + window.fsPhysics.rubberband(offset - max, this.frame.clientWidth);
      }

      this._spring.jump(offset);
      this._dragOffset = offset;
    }

    _onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.classList.remove('is-dragging');

      var velocity = this._velocity ? this._velocity.velocity(performance.now()) : 0;
      var current = this._dragOffset == null ? this._spring.value : this._dragOffset;
      if (this.track) this._spring.jump(current);

      var projected = current - window.fsPhysics.project(velocity);

      var best = 0;
      var bestDistance = Infinity;
      var lastPos = this.allSlides ? this.realCount + 1 : this.realCount - 1;
      for (var pos = 0; pos <= lastPos; pos++) {
        var distance = Math.abs(this._offsetAt(pos) - projected);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = pos;
        }
      }

      this._dragOffset = null;
      this.pos = best;
      this.index = this._indexForPos(best);
      this._updateSlides();

      var bounce = Math.abs(velocity) > 350;
      this._animateToPos(this.pos, -velocity, bounce, this._finishWrap);
      this._start();
    }
  }

  customElements.define('football-slideshow', FootballSlideshow);

  document.addEventListener('shopify:section:load', function (event) {
    var el = event.target && event.target.querySelector
      ? event.target.querySelector('football-slideshow')
      : null;
    if (el && typeof el._init === 'function') el._init();
  });
})();
